import { useState } from "react";
import "./CustomModal.css";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { FaUser, FaTimes } from "react-icons/fa";
import { API_BASE_URL, ADMIN_BASE_URL } from "./config";

// ✅ Validation schema
const validationSchema = Yup.object().shape({
  user_name: Yup.string()
    .required("Username is required")
    .min(3, "Minimum 3 characters"),
  refer_user_name: Yup.string().nullable(),
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone_number: Yup.string()
    .nullable()
    .matches(/^[+()\d\s-]{7,20}$/, "Phone number must contain only digits, spaces, +, (), or - and be 7–20 characters long"),
  dob: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    })
    .max(new Date(), "Date of birth cannot be in the future"),
  country: Yup.string().nullable(),
  state: Yup.string().nullable(),
  city: Yup.string().nullable(),
  province: Yup.string().nullable(),
  zip: Yup.string().nullable().matches(/^\d*$/, "ZIP must be numeric"),
  streetAddress: Yup.string().required("Street address is required."),
  txc_coin_wallet_address: Yup.string().nullable(),
  streetAddress2: Yup.string().nullable(),
  homestatus: Yup.string().nullable(),
  employmentstatus: Yup.string().nullable(),
  householdincome: Yup.number()
    .typeError("Income must be a number")
    .nullable()
    .positive("Income must be positive"),
  social_media: Yup.string().nullable(),
  petstatus: Yup.string().nullable(),
  pet_type: Yup.string().nullable(),
  feedback: Yup.string().nullable(),
  // password: Yup.string()
  // .required("Password is required")
  // .min(8, "Password must be at least 8 characters long")
  // .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
  // .matches(/[a-z]/, "Password must contain at least one lowercase letter")
  // .matches(/\d/, "Password must contain at least one number")
  // .matches(/[@$!%*?&^#()_\-]/, "Password must contain at least one special character"),
});

const CustomModal = ({ show, onClose, data, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editedData, setEditedData] = useState({ ...data });
  const [isUserUsernameValid, setIsUserUsernameValid] = useState(null);
  const [sponserUserId, setSponserUserId] = useState(null);
  const [isUserUsernameChecking, setIsUserUsernameChecking] = useState(false);
  const [isSponsorUsernameValid, setIsSponsorUsernameValid] = useState(false);
  const [isSponsorUsernameChecking, setIsSponsorUsernameChecking] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const isAdmin = localStorage.getItem('isAdmin') === '1';
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...data,
      dob: data?.dob ? data.dob.slice(0, 10) : null,
    },
  });

  if (!show) return null;
  const handleEdit = () => {
    setEditedData({ ...data, dob: data?.dob ? data.dob.slice(0, 10) : null })
    reset({
      ...data,
      dob: data?.dob ? data.dob.slice(0, 10) : null,
    });
    setIsEditing(true);
    checkSponsorUsername(data?.refer_user_name);
    checkUserUsername(data?.user_name);
  };


  // ✅ Async username check
  const checkUserUsername = async (username) => {

    // If username is empty, mark invalid
    if (!username) {
      setIsUserUsernameValid(false);
      return;
    }

    // Skip checking if unchanged
    if (username === data.user_name) {
      setIsUserUsernameValid(true);
      return;
    }

    setIsUserUsernameChecking(true);
    try {
      const response = await fetch(`${API_BASE_URL}/check-username?username=${encodeURIComponent(username)}`);
      const result = await response.json();

      if (!response.ok) {
        setIsUserUsernameValid(false);
      } else if (!result.exists) {
        // ✅ Username is available
        setIsUserUsernameValid(true);
      } else {
        // ❌ Username already taken
        setIsUserUsernameValid(false);
      }
    } catch (error) {
      console.error("Error checking username:", error);
      setIsUserUsernameValid(false);
    } finally {
      setIsUserUsernameChecking(false);
    }
  };

  // ✅ Async sponsor check
  const checkSponsorUsername = async (username) => {
    if (!username) {
      setIsSponsorUsernameValid(true);
      return;
    }

    setIsSponsorUsernameChecking(true);
    try {
      const response = await fetch(`${API_BASE_URL}/verify-username?username=${encodeURIComponent(username)}`);
      const result = await response.json();

      if (response.status === 404) {
        // Username NOT found
        setIsSponsorUsernameValid(false);
      } else if (response.ok && result.exists) {
        setSponserUserId(result?.data?.userId)
        setIsSponsorUsernameValid(true);
      } else {
        setIsSponsorUsernameValid(false);
      }
    } catch (error) {
      console.error("Error checking username:", error);
      setIsSponsorUsernameValid(false);
    } finally {
      setIsSponsorUsernameChecking(false);
    }
  };
  // const handleBlur = async (refer_user_name) => {
  //   const username = refer_user_name?.trim();
  //   if (!username) {
  //     setIsSponsorUsernameValid(true);
  //     return;
  //   }

  //   setIsSponsorUsernameChecking(true);
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/verify-username?username=${encodeURIComponent(username)}`);
  //     const result = await response.json();

  //     if (response.status === 404) {
  //       // Username NOT found
  //       setIsSponsorUsernameValid(false);
  //     } else if (response.ok && result.exists) {
  //       setSponserUserId(result?.data?.userId)
  //       setIsSponsorUsernameValid(true);
  //     } else {
  //       setIsSponsorUsernameValid(false);
  //     }
  //   } catch (error) {
  //     console.error("Error checking username:", error);
  //     setIsSponsorUsernameValid(false);
  //   } finally {
  //     setIsSponsorUsernameChecking(false);
  //   }
  // };

  // const handleUserUsernameBlur = async (user_name) => {
  //   const username = user_name?.trim();

  //   // If username is empty, mark invalid
  //   if (!username) {
  //     setIsUserUsernameValid(false);
  //     return;
  //   }

  //   // Skip checking if unchanged
  //   if (username === data.user_name) {
  //     setIsUserUsernameValid(true);
  //     return;
  //   }

  //   setIsUserUsernameChecking(true);
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/check-username?username=${encodeURIComponent(username)}`);
  //     const result = await response.json();

  //     if (!response.ok) {
  //       setIsUserUsernameValid(false);
  //     } else if (!result.exists) {
  //       // ✅ Username is available
  //       setIsUserUsernameValid(true);
  //     } else {
  //       // ❌ Username already taken
  //       setIsUserUsernameValid(false);
  //     }
  //   } catch (error) {
  //     console.error("Error checking username:", error);
  //     setIsUserUsernameValid(false);
  //   } finally {
  //     setIsUserUsernameChecking(false);
  //   }
  // };

  const formatValue = (val) => (val !== null && val !== undefined && val !== "") ? val : "—";

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "refer_user_name" && value === "") {
      setIsSponsorUsernameValid(true)
    }
    else if (name === "user_created_at" || name === "dob") {
      const isoValue = value ? new Date(value).toISOString() : null;
      setEditedData((prev) => ({ ...prev, [name]: isoValue }));
    } else {
      // Default handling for other inputs
      setEditedData((prev) => ({ ...prev, [name]: value }));
    }
    // setIsSponsorUsernameValid(null);
  };

  const onFormSubmit = async (formValues) => {
    console.log('formValues.dob', formValues.dob)
    if (!isSponsorUsernameValid) {
      alert("❌ Please enter a valid sponsor username before submitting.");
      return;
    }
    if (!isUserUsernameValid) {
      alert("❌ Please enter a valid username before submitting.");
      return;
    }
    try {
      setLoading(true);

      const payload = {
        first_name: formValues.first_name?.trim(),
        last_name: formValues.last_name?.trim(),
        // password: formValues.password?.trim(),
        email: formValues.email?.trim(),
        phone_number: formValues.phone_number?.trim(),
        user_name: formValues.user_name?.trim(),
        country: formValues.country?.trim(),
        state: formValues.state?.trim(),
        city: formValues.city?.trim(),
        province: formValues.province?.trim(),
        zip: formValues.zip?.trim(),
        dob: formValues.dob
          ? new Date(formValues.dob).toISOString().slice(0, 10).replace("T", " ")
          : null,
        homestatus: formValues.homestatus?.trim(),
        employmentstatus: formValues.employmentstatus?.trim(),
        householdincome: formValues.householdincome,
        streetAddress: formValues.streetAddress?.trim(),
        streetAddress2: formValues.streetAddress2?.trim(),
        petstatus: formValues.petstatus?.trim(),
        feedback: formValues.feedback?.trim(),
        userReferId: sponserUserId ?? data?.user_refer_id,
        origin_lgns_o_invite_link: formValues.origin_lgns_o_invite_link?.trim(),
      };

      // ✅ Call parent-provided onSubmit handler (as in your props)
      await onSubmit(data?.id, JSON.stringify(payload), sponserUserId);

      // alert("✅ User updated successfully!");
      setIsEditing(false);
      setEditedData({});
    } catch (error) {
      console.error("Error updating user:", error);
      alert("❌ Error updating user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!isSponsorUsernameValid) {
  //     alert("Please enter a valid sponsor username before submitting.");
  //     return;
  //   }
  //   if (!isUserUsernameValid) {
  //     alert("Please enter a valid username before submitting. Make sure username should not be blank");
  //     return;
  //   }
  //   onSubmit(data?.id, JSON.stringify({
  //     first_name: editedData.first_name,
  //     last_name: editedData.last_name,
  //     email: editedData.email,
  //     phone_number: editedData.phone_number,
  //     user_name: editedData.user_name,
  //     country: editedData.country,
  //     state: editedData.state,
  //     city: editedData.city,
  //     province: editedData.province,
  //     zip: editedData.zip,
  //     dob: editedData.dob
  //       ? new Date(editedData.dob).toISOString().slice(0, 10)
  //       : null,
  //     homestatus: editedData.homestatus,
  //     employmentstatus: editedData.employmentstatus,
  //     householdincome: editedData.householdincome,
  //     streetAddress: editedData.streetAddress,
  //     streetAddress2: editedData.streetAddress2,
  //     petstatus: editedData.petstatus,
  //     feedback: editedData.feedback,
  //     userReferId: sponserUserId ?? editedData?.user_refer_id,
  //   }), sponserUserId)
  //   // try {
  //   //   setLoading(true);
  //   //   const token = localStorage.getItem('token');
  //   //   const response = await fetch(`${ADMIN_BASE_URL}/adminapi/user/${data.id}`, {
  //   //     method: "PUT",
  //   //     headers: {
  //   //       Authorization: `Bearer ${token}`,
  //   //       'Content-Type': 'application/json',
  //   //     },
  //   //     body: JSON.stringify({
  //   //       first_name: editedData.first_name,
  //   //       last_name: editedData.last_name,
  //   //       email: editedData.email,
  //   //       phone_number: editedData.phone_number,
  //   //       user_name: editedData.user_name,
  //   //       country: editedData.country,
  //   //       state: editedData.state,
  //   //       city: editedData.city,
  //   //       province: editedData.province,
  //   //       zip: editedData.zip,
  //   //       dob: editedData.dob
  //   //         ? new Date(editedData.dob).toISOString().slice(0, 10)
  //   //         : null,
  //   //       homestatus: editedData.homestatus,
  //   //       employmentstatus: editedData.employmentstatus,
  //   //       householdincome: editedData.householdincome,
  //   //       streetAddress: editedData.streetAddress,
  //   //       streetAddress2: editedData.streetAddress2,
  //   //       petstatus: editedData.petstatus,
  //   //       feedback: editedData.feedback,
  //   //       userReferId: sponserUserId ?? editedData?.user_refer_id,
  //   //     }),
  //   //   });

  //   //   const result = await response.json();
  //   //   if (!response.ok) {
  //   //     alert(result.message || "Failed to update user");
  //   //     setLoading(false);
  //   //     return;
  //   //   }

  //   //   alert("✅ User updated successfully!");
  //   //   setIsEditing(false);
  //   // } catch (error) {
  //   //   console.error("Error updating user:", error);
  //   //   alert("❌ Error updating user. Please try again.");
  //   // } finally {
  //   //   setLoading(false);
  //   // }
  //   setIsEditing(false);
  //   setEditedData({});
  // };

  const renderError = (field) =>
    errors[field] ? <small className="text-red">{errors[field].message}</small> : null;

  // Handle password reset
  const handleResetPassword = async () => {
    if (!data?.id) {
      alert("❌ User ID is missing. Cannot reset password.");
      return;
    }

    const confirmReset = window.confirm(
      `Are you sure you want to send a password reset email to ${data.email || "this user"}?`
    );

    if (!confirmReset) return;

    try {
      setIsResettingPassword(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${ADMIN_BASE_URL}/adminapi/users/${data.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        alert(`❌ Error: ${result.message || 'Failed to send password reset email'}`);
        return;
      }

      alert(`✅ Password reset email sent successfully to ${data.email || "the user"}!`);
    } catch (error) {
      console.error("Error resetting password:", error);
      alert("❌ Error sending password reset email. Please try again.");
    } finally {
      setIsResettingPassword(false);
    }
  };


  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal-container">
        <div className="custom-modal-header">
          <h2><FaUser className="modal-icon" /> User Information</h2>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", alignItems: "center" }}>
            {isAdmin && <div className="desktop-edit-controls" style={{ display: "flex", gap: "10px", justifyContent: "center", alignItems: "center" }}>
              {isEditing ? (
                <>
                  <button className="white-btn" type="submit" onClick={handleSubmit(onFormSubmit)} disabled={loading}>Save Changes</button>
                  <button className="white-btn" onClick={() => {
                    setIsEditing(false);
                    setEditedData({})
                  }}>Cancel Edit</button>
                </>
              ) : (
                <button className="white-btn" onClick={handleEdit}>Edit</button>
              )}
            </div>}
            <button className="modal-close-btn" style={{ display: "flex" }} onClick={() => {
              onClose();
              setIsEditing(false);
              setEditedData({})
            }}><FaTimes /></button>
          </div>
        </div>
        <div className="custom-modal-body">
          <div>
            {/* MOBILE EDIT CONTROLS - visible only on small screens via CSS */}
            <div className="mobile-edit-controls">
              {isEditing ? (
                <>
                  <button className="primary-btn" type="submit" onClick={handleSubmit(onFormSubmit)} disabled={loading}>Save Changes</button>
                  <button className="primary-btn" onClick={() => {
                    setIsEditing(false);
                    setEditedData({});
                  }}>Cancel Edit</button>
                </>
              ) : (
                <button className="primary-btn" onClick={handleEdit}>Edit</button>
              )}
            </div>
            {data?.id && (
              <button
                className="text-btn"
                onClick={handleResetPassword}
                disabled={isResettingPassword}
                style={{
                  marginBottom: '10px',
                  marginLeft: "auto",
                  display: "flex"
                }}
              >
                {isResettingPassword ? 'Sending...' : 'Send Password Reset Email'}
              </button>
            )}
          </div>
          {data ? (
            <div className="modal-grid-2col">
              <div className="modal-item"><label>First Name:</label> {isEditing ? (<>
                <input
                  {...register("first_name")}
                  className="custom-input"
                />
                {renderError("first_name")}
              </>
              ) : (
                <span>{formatValue(data.first_name)}</span>
              )}</div>
              <div className="modal-item"><label>Username:</label> {isEditing ? (<>
                <input
                  {...register("user_name")}
                  onBlur={(e) => checkUserUsername(e.target.value)}
                  className="custom-input"
                // required
                />
                {isUserUsernameChecking && (
                  <p style={{ color: "gray", margin: 0, fontSize: 12 }}>Checking username...</p>
                )}
                {isUserUsernameValid === false && (
                  <p style={{ color: "red", margin: 0, fontSize: 12 }}>Username is not available.</p>
                )}
                {isUserUsernameValid === true && (
                  <p style={{ color: "green", margin: 0, fontSize: 12 }}>Username is available.</p>
                )}
                {renderError("user_name")}
              </>
              ) : (
                <span>{formatValue(data.user_name)}</span>
              )}</div>
              <div className="modal-item"><label>Last Name:</label> {isEditing ? (<>
                <input
                  {...register("last_name")}
                  className="custom-input"
                />
                {renderError("last_name")}
              </>
              ) : (
                <span>{formatValue(data.last_name)}</span>
              )}</div>
              <div className="modal-item"><label>Email:</label> {isEditing ? (
                <><input {...register("email")} className="custom-input" />
                  {renderError("email")}</>
              ) : (
                <span>{formatValue(data.email)}</span>
              )}</div>
              <div className="modal-item"><label>Sponser Username:</label> {isEditing ? (
                <>
                  <input
                    // name="refer_user_name"
                    // type="text"
                    // value={editedData.refer_user_name}
                    // onChange={handleEditChange}

                    {...register("refer_user_name")}
                    // onBlur={() => checkSponsorUsername(editedData.refer_user_name)}
                    onBlur={(e) => (e.target?.value?.trim() === "" || editedData.refer_user_name || editedData.refer_user_name !== "" || editedData?.refer_user_name !== null) ? checkSponsorUsername(e.target.value) : {}}
                    className="custom-input"
                  />
                  {isSponsorUsernameChecking && <p style={{ color: "gray", margin: 0, fontSize: 12 }}>Checking username...</p>}
                  {isSponsorUsernameValid === false && (
                    <p style={{ color: "red", margin: 0, fontSize: 12 }}>Sponsor username not found.</p>
                  )}
                  {(isSponsorUsernameValid === true && editedData?.refer_user_name !== "" && editedData?.refer_user_name !== null) && (
                    <p style={{ color: "green", margin: 0, fontSize: 12 }}>Sponsor username is available.</p>
                  )}
                  {renderError("refer_user_name")}
                </>
              ) : (
                <span>{formatValue(data.refer_user_name)}</span>
              )}</div>
              {/* <div className="modal-item"><label>Password:</label> {isEditing ? (<>
                <input
                  {...register("password")}
                  className="custom-input"
                />
                {renderError("password")}
              </>
              ) : (
                <span>{formatValue(data.password)}</span>
              )}</div> */}
              <div className="modal-item"><label>Phone:</label> {isEditing ? (
                <><input {...register("phone_number")} className="custom-input" />
                  {renderError("phone_number")}</>
              ) : (
                <span>{formatValue(data.phone_number)}</span>
              )}</div>
              <div className="modal-item"><label>Created On:</label>
                {/* {isEditing ? <input
                type="datetime-local"
                name="user_created_at"
                value={
                  editedData.user_created_at
                    ? new Date(editedData.user_created_at).toISOString().slice(0, 16)
                    : ""
                }
                onChange={handleEditChange}
                className="custom-input"
              /> :  */}
                <span>{new Date(data.user_created_at).toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false,
                  timeZone: 'America/Chicago'
                })}</span>
                {/* } */}
              </div>
              <div className="modal-item"><label>DOB:</label> {isEditing ? (<>
                <input
                  type="date"
                  name="dob"
                  // value={editedData.dob
                  //   ? new Date(editedData.dob).toISOString().slice(0, 10)
                  //   : ""}
                  // onChange={handleEditChange}
                  {...register("dob")}
                  max={new Date(
                    new Date().toLocaleString("en-US", { timeZone: "America/Chicago" })
                  )
                    .toISOString()
                    .split("T")[0]}
                  className="custom-input"
                />
                {renderError("dob")}
              </>) : <span>
                {data.dob
                  ? new Date(
                    new Date(data.dob).toLocaleString("en-US", { timeZone: "America/Chicago" })
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                  : "—"}
              </span>}</div>
              <div className="modal-item"><label>Street Address:</label> {isEditing ? (<>
                <input
                  {...register("streetAddress")}
                  className="custom-input"
                />
                {renderError("streetAddress")}
              </>
              ) : <span>{formatValue(data.streetAddress)}</span>}</div>
              <div className="modal-item"><label>Street Address 2:</label> {isEditing ? (
                <input
                  {...register("streetAddress2")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.streetAddress2)}</span>}</div>
              <div className="modal-item"><label>City:</label> {isEditing ? (
                <input
                  {...register("city")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.city)}</span>}</div>
              <div className="modal-item"><label>State:</label> {isEditing ? (
                <input
                  {...register("state")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.state)}</span>}</div>
              <div className="modal-item"><label>Province:</label> {isEditing ? (
                <input
                  {...register("province")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.province)}</span>}</div>
              <div className="modal-item"><label>Country:</label> {isEditing ? (
                <input
                  {...register("country")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.country)}</span>}</div>
              <div className="modal-item"><label>ZIP:</label> {isEditing ? (
                <input
                  {...register("zip")}
                  className="custom-input"
                />
              ) :
                // <span>{formatValue(data.zip)}</span>}</div>
                // <div className="modal-item"><label>TXC COIN WALLET ADDRESS<br />
                //   (for daily crypto mining deposits):</label> {isEditing ? (
                //     <input
                //       {...register("txc_coin_wallet_address")}
                //       className="custom-input"
                //     />
                <span>{formatValue(data.zip)}</span>}</div>
              <div className="modal-item"><label>ORIGIN LGNS ("O") - Invite Link<br />
                (link is authenticated by VIRON Admins only):</label> {isEditing ? (
                  <input
                    {...register("origin_lgns_o_invite_link")}
                    className="custom-input"
                  />

                ) : <span>{formatValue(data.origin_lgns_o_invite_link)}</span>}</div>
              <div className="modal-item"><label>Home Status:</label> {isEditing ? (
                <input
                  {...register("homestatus")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.homestatus)}</span>}</div>
              <div className="modal-item"><label>Income:</label> {isEditing ? (
                <input
                  type="number"
                  {...register("householdincome")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.householdincome)}</span>}</div>
              <div className="modal-item"><label>Employment:</label> {isEditing ? (
                <input
                  {...register("employmentstatus")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.employmentstatus)}</span>}</div>
              <div className="modal-item"><label>Social Media:</label> {isEditing ? (
                <input
                  {...register("social_media")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.social_media)}</span>}</div>
              <div className="modal-item"><label>Pet Status:</label> {isEditing ? (
                <input
                  {...register("petstatus")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.petstatus)}</span>}</div>
              <div className="modal-item"><label>Pet Type:</label> {isEditing ? (
                <input
                  {...register("pet_type")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.pet_type)}</span>}</div>
              <div className="modal-item"><label>Feedback:</label> {isEditing ? (
                <input
                  {...register("feedback")}
                  className="custom-input"
                />
              ) : <span>{formatValue(data.feedback)}</span>}</div>
              <div className="modal-item"><label>Trasaction type:</label>
                {/* {isEditing ? (
                <input
                  type="text"
                  name="type"
                  value={editedData.transaction?.type}
                  onChange={handleEditChange}
                  className="custom-input"
                />
              ) :  */}
                <span>{formatValue(data?.transaction?.type)}</span>
                {/* } */}
              </div>
              <div className="modal-item"><label>Transaction value:</label>
                {/* {isEditing ? (
                <input
                  type="text"
                  name="value"
                  value={editedData?.transaction?.value}
                  onChange={handleEditChange}
                  className="custom-input"
                />
              ) :  */}
                <span>{formatValue(data?.transaction?.value)}</span>
                {/* } */}
              </div>
              <div className="modal-item"><label>Transaction Time/Date:</label>
                {/* {isEditing ? <input
                type="datetime-local"
                name="created_at"
                value={
                  editedData.transaction?.created_at
                    ? new Date(editedData.transaction?.created_at).toISOString().slice(0, 16)
                    : ""
                }
                onChange={handleEditChange}
                className="custom-input"
              /> :  */}
                <span>   {data?.transaction?.created_at
                  ? new Date(data?.transaction.created_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })
                  : '—'}</span>
                {/* } */}
              </div>
              <div className="modal-item"><label>DS Sponsor</label>
                <span>{data?.dsSponsor || '—'}</span>
              </div>
            </div>
          ) : (
            <p class=" ">No data available.</p>
          )}
        </div>
        <div className="custom-modal-footer">
          {/* {isEditing ? (
            <>
              <button className="primary-btn" onClick={handleSubmit}>Save Changes</button>
              <button className="primary-btn" onClick={() => {
                setIsEditing(false);
                setEditedData({})
              }}>Cancel Edit</button>
            </>
          ) : (
            <button className="primary-btn" onClick={() => setIsEditing(true)}>Edit</button>
          )} */}
          <button className="primary-btn" onClick={() => {
            onClose();
            setIsEditing(false);
            setEditedData({})
          }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default CustomModal;
